import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PwaInstallService } from '../../services/pwa-install.service';

@Component({
  selector: 'app-pwa-install-section',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="pwa-install-section">
      
      <!-- Estado de instalación -->
      <div class="install-status-card" [ngClass]="getStatusClass()">
        <div class="status-icon">{{ getStatusIcon() }}</div>
        <div class="status-content">
          <h3 class="status-title">{{ getStatusTitle() }}</h3>
          <p class="status-description">{{ getStatusDescription() }}</p>
        </div>
        <div class="status-actions" *ngIf="!isInstalled">
          <button class="btn-primary-install" (click)="tryInstall()" *ngIf="canInstall">
            📱 Instalar Ahora
          </button>
        </div>
      </div>

      <!-- Instrucciones manuales -->
      <div class="manual-install-card" *ngIf="!isInstalled">
        <div class="card-header">
          <h4 class="card-title">📖 Instrucciones de Instalación</h4>
          <p class="card-subtitle">Para {{ platformInfo.platform }}</p>
        </div>
        
        <div class="instructions-content">
          <div class="platform-icon">
            <span *ngIf="platformInfo.platform === 'iOS'">🍎</span>
            <span *ngIf="platformInfo.platform === 'Android'">🤖</span>
            <span *ngIf="platformInfo.platform === 'Desktop'">💻</span>
          </div>
          
          <ol class="instructions-list">
            <li *ngFor="let instruction of platformInfo.instructions; index as i" 
                class="instruction-item"
                [class.completed]="completedSteps.includes(i)">
              <div class="step-number">{{ i + 1 }}</div>
              <div class="step-content">
                <span class="step-text">{{ instruction }}</span>
                <button class="step-check" 
                        (click)="toggleStep(i)"
                        [class.checked]="completedSteps.includes(i)">
                  {{ completedSteps.includes(i) ? '✅' : '⭕' }}
                </button>
              </div>
            </li>
          </ol>
          
          <div class="instructions-footer" *ngIf="completedSteps.length === platformInfo.instructions.length">
            <div class="success-message">
              <span class="success-icon">🎉</span>
              <span class="success-text">¡Perfecto! BarClik debería estar instalada ahora</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Funciones PWA -->
      <div class="pwa-features-card">
        <div class="card-header">
          <h4 class="card-title">⚡ Funciones de la App</h4>
          <p class="card-subtitle">Qué obtienes al instalar BarClik</p>
        </div>
        
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">🚀</div>
            <h5 class="feature-title">Acceso Rápido</h5>
            <p class="feature-description">Abre BarClik directamente desde tu pantalla de inicio</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">📱</div>
            <h5 class="feature-title">Experiencia Nativa</h5>
            <p class="feature-description">Se comporta como una aplicación real de tu dispositivo</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">🔔</div>
            <h5 class="feature-title">Notificaciones</h5>
            <p class="feature-description">Recibe alertas sobre pedidos y promociones especiales</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">⚡</div>
            <h5 class="feature-title">Offline</h5>
            <p class="feature-description">Consulta tu historial y notas sin conexión a internet</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">🔐</div>
            <h5 class="feature-title">Seguro</h5>
            <p class="feature-description">Datos protegidos con las mismas medidas que los bancos</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">🎯</div>
            <h5 class="feature-title">Personalizado</h5>
            <p class="feature-description">Adaptado a tus preferencias y historial de pedidos</p>
          </div>
        </div>
      </div>

      <!-- Solución de problemas -->
      <div class="troubleshooting-card">
        <div class="card-header">
          <h4 class="card-title">🔧 Solución de Problemas</h4>
          <p class="card-subtitle">Si tienes dificultades para instalar</p>
        </div>
        
        <div class="troubleshooting-content">
          <details class="troubleshooting-item">
            <summary class="troubleshooting-question">
              ❓ No veo la opción "Instalar" o "Añadir a pantalla de inicio"
            </summary>
            <div class="troubleshooting-answer">
              <p>Esto puede ocurrir por varios motivos:</p>
              <ul>
                <li>Asegúrate de estar usando Chrome, Safari o Edge</li>
                <li>Verifica que tengas la última versión del navegador</li>
                <li>En iOS, solo Safari permite instalar aplicaciones web</li>
                <li>Si ya instalaste la app, la opción no aparecerá</li>
              </ul>
            </div>
          </details>
          
          <details class="troubleshooting-item">
            <summary class="troubleshooting-question">
              ❓ La aplicación no funciona correctamente después de instalar
            </summary>
            <div class="troubleshooting-answer">
              <p>Prueba estas soluciones:</p>
              <ul>
                <li>Cierra completamente la app y vuelve a abrirla</li>
                <li>Verifica tu conexión a internet</li>
                <li>Actualiza la aplicación desde la tienda de tu dispositivo</li>
                <li>Si persiste el problema, desinstala y vuelve a instalar</li>
              </ul>
            </div>
          </details>
          
          <details class="troubleshooting-item">
            <summary class="troubleshooting-question">
              ❓ ¿Cómo desinstalo la aplicación?
            </summary>
            <div class="troubleshooting-answer">
              <p>Para desinstalar BarClik:</p>
              <ul>
                <li><strong>iOS:</strong> Mantén presionado el icono y selecciona "Eliminar app"</li>
                <li><strong>Android:</strong> Mantén presionado el icono y arrastra a "Desinstalar"</li>
                <li><strong>Desktop:</strong> Ve a Configuración del navegador > Aplicaciones instaladas</li>
              </ul>
            </div>
          </details>
        </div>
      </div>

      <!-- Acciones adicionales -->
      <div class="additional-actions" *ngIf="isInstalled">
        <button class="btn-secondary" (click)="checkForUpdates()">
          🔄 Buscar Actualizaciones
        </button>
        
        <button class="btn-secondary" (click)="resetInstallState()" 
                *ngIf="showDeveloperOptions">
          🔧 Resetear Estado (Dev)
        </button>
      </div>

    </div>
  `,
  styleUrl: './pwa-install-section.component.scss'
})
export class PwaInstallSectionComponent implements OnInit {
  isInstalled = false;
  canInstall = false;
  platformInfo: { platform: string; instructions: string[] } = {
    platform: 'Unknown',
    instructions: []
  };
  completedSteps: number[] = [];
  showDeveloperOptions = false;

  constructor(private pwaInstallService: PwaInstallService) {}

  ngOnInit(): void {
    this.isInstalled = this.pwaInstallService.isInstalled();
    this.canInstall = this.pwaInstallService.isInstallAvailable();
    this.platformInfo = this.pwaInstallService.getPlatformInstructions();
    
    // Mostrar opciones de desarrollo en entorno local
    this.showDeveloperOptions = window.location.hostname === 'localhost' || 
                                window.location.hostname === '127.0.0.1';

    // Cargar pasos completados del localStorage
    const savedSteps = localStorage.getItem('pwa-install-steps');
    if (savedSteps) {
      this.completedSteps = JSON.parse(savedSteps);
    }
  }

  getStatusClass(): string {
    if (this.isInstalled) return 'status-installed';
    if (this.canInstall) return 'status-available';
    return 'status-manual';
  }

  getStatusIcon(): string {
    if (this.isInstalled) return '✅';
    if (this.canInstall) return '📱';
    return '📖';
  }

  getStatusTitle(): string {
    if (this.isInstalled) return '¡App Instalada!';
    if (this.canInstall) return 'Instalar BarClik';
    return 'Instalación Manual';
  }

  getStatusDescription(): string {
    if (this.isInstalled) {
      return 'BarClik está instalada y funcionando correctamente en tu dispositivo.';
    }
    if (this.canInstall) {
      return 'Tu navegador permite instalar BarClik como una aplicación. ¡Hazlo ahora!';
    }
    return 'Sigue las instrucciones para añadir BarClik a tu pantalla de inicio.';
  }

  async tryInstall(): Promise<void> {
    const success = await this.pwaInstallService.showInstallPrompt();
    
    if (success) {
      this.isInstalled = true;
      this.canInstall = false;
      this.showSuccessToast('¡BarClik instalada correctamente! 🎉');
    }
  }

  toggleStep(stepIndex: number): void {
    const index = this.completedSteps.indexOf(stepIndex);
    
    if (index > -1) {
      this.completedSteps.splice(index, 1);
    } else {
      this.completedSteps.push(stepIndex);
    }
    
    // Guardar progreso en localStorage
    localStorage.setItem('pwa-install-steps', JSON.stringify(this.completedSteps));
    
    // Si completó todos los pasos, mostrar mensaje
    if (this.completedSteps.length === this.platformInfo.instructions.length) {
      setTimeout(() => {
        this.showSuccessToast('¡Excelente! Deberías ver BarClik en tu pantalla de inicio 🎯');
      }, 500);
    }
  }

  async checkForUpdates(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        this.showSuccessToast('✅ Verificación de actualizaciones completada');
      } catch (error) {
        this.showErrorToast('❌ Error al verificar actualizaciones');
      }
    }
  }

  resetInstallState(): void {
    this.pwaInstallService.resetInstallState();
    localStorage.removeItem('pwa-install-steps');
    this.completedSteps = [];
    this.isInstalled = false;
    this.canInstall = this.pwaInstallService.isInstallAvailable();
    this.showSuccessToast('🔧 Estado de instalación reseteado');
  }

  private showSuccessToast(message: string): void {
    this.createToast(message, 'success');
  }

  private showErrorToast(message: string): void {
    this.createToast(message, 'error');
  }

  private createToast(message: string, type: 'success' | 'error'): void {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    
    const styles = type === 'success' 
      ? 'background: #10b981; color: white;'
      : 'background: #ef4444; color: white;';
      
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      ${styles}
      padding: 1rem 1.5rem;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      font-weight: 600;
      animation: slideInRight 0.3s ease;
      max-width: 300px;
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}